import { useState, useRef, useCallback } from "react";

// Tracks the pixel size of an element via ResizeObserver.
// Returns [ref, { width, height }]; attach ref to the element you want to measure.
// Used to convert between fractional note positions (0..1) and pixels so a board
// renders consistently across laptops, phones, and tablets of any size.
//
// IMPORTANT: this uses a *callback ref* rather than useRef + useEffect([]). The
// elements we measure (the caregiver board, the kiosk board) are rendered
// conditionally — the component first renders a different element (empty-state
// or pairing screen) and only later swaps in the element we want to measure. A
// mount-only useEffect would run while the target element isn't in the DOM yet
// and never re-attach the observer when it appears, leaving size stuck at 0
// (which hid every note). A callback ref fires whenever the node mounts, so
// measurement always happens once the real element is present.
export default function useElementSize() {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const observerRef = useRef(null);

  const ref = useCallback((node) => {
    // Tear down any observer attached to a previous node.
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      const measure = () =>
        setSize({ width: node.clientWidth, height: node.clientHeight });
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      observerRef.current = observer;
    }
  }, []);

  return [ref, size];
}
